/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import NextImage from '@/components/NextImage';
import { Hidden, Link, Text, View } from '@az/starc-ui';
import { type VideoResponseModel } from '@/api/types/products-types';
import { useLabels } from '@/hooks/useLabels';
import { useMediaQuery } from '@/hooks/useMediaQuery';

type Props = {
  data?: VideoResponseModel;
  onClick: () => void;
};

const imageSrc = 'https://www.autozone.com/cdn/images/B2C/US/media/icons/D_Banner_Image.jpg';
const bannerIcon = 'https://www.autozone.com/cdn/images/B2C/US/media/icons/D_Banner_Icon.svg';

const labelMap = {
  label_YMMESpecificInstallVideoBanner_h1_1of2: 'label_YMMESpecificInstallVideoBanner_h1_1of2',
  label_YMMESpecificInstallVideoBanner_h1_2of2: 'label_YMMESpecificInstallVideoBanner_h1_2of2',
  label_YMMESpecificInstallVideoBanner_body: 'label_YMMESpecificInstallVideoBanner_body',
};

export const YMMESpecificInstallVideoBannerV2 = ({ data, onClick }: Props) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery((theme) => theme.breakpoints.only('md'));
  const labels = useLabels(labelMap);
  const location = data?.sylvaniaVideos?.[0]?.location
    ? (data?.locations?.[data.sylvaniaVideos[0].location] ?? '')
    : '';
  const vehicle = [data?.vehicleYear, data?.vehicleMake, data?.vehicleModel]
    .filter(Boolean)
    .join(' ');
  const headerText = [
    labels.label_YMMESpecificInstallVideoBanner_h1_1of2,
    location && location.toLowerCase(),
    labels.label_YMMESpecificInstallVideoBanner_h1_2of2,
    vehicle,
  ]
    .filter((part) => typeof part === 'string' && part.trim())
    .join(' ');

  const thumbnailSize = isMobile ? 35 : isTablet ? 70 : 60;

  return (
    <Link variant="plain" onClick={onClick}>
      <View
        padding={{ s: 2, m: 4, l: 2, xl: 4 }}
        attributes={{ style: { backgroundColor: 'var(--st-color-gray-100)' } }}
        direction="row"
        height="100%"
        align="center"
      >
        <Hidden hide={{ l: true }}>
          <View.Item>
            <View padding={1}>
              <NextImage
                src={bannerIcon}
                height={isMobile ? 32.67 : 49}
                width={isMobile ? 50 : 75}
              />
            </View>
          </View.Item>
        </Hidden>
        <View.Item
          gapBefore={{ s: 3, m: 4, xl: 4 }}
          attributes={{ style: { flex: 1, minWidth: 0 } }}
        >
          <View direction="column">
            <Text variant="large-body" weight="bold">
              {headerText}
            </Text>
            <View.Item gapBefore={1}>
              <Text variant="small-body">{labels.label_YMMESpecificInstallVideoBanner_body}</Text>
            </View.Item>
          </View>
        </View.Item>
        <View.Item
          gapBefore={{ s: 3, m: 4, l: 4, xl: 4 }}
          attributes={{ style: { flexShrink: 0 } }}
        >
          <NextImage src={imageSrc} height={thumbnailSize} width={thumbnailSize} />
        </View.Item>
      </View>
    </Link>
  );
};
